/*
  biobio-tone-finder.js — <biobio-tone-finder>
  Plano: docs/plans/2026-06-10-encontre-seu-tom.md (fase 2).

  Passo 1 (faixa de profundidade) -> passo 2 (subtom) -> resultado 1-2 tons.
  Os candidatos por faixa vem do atributo data-faixa (codigos por virgula),
  os dados das variantes do JSON inline. A regra de desempate usa o campo
  subtom OFICIAL dos metafields: filtra por afinidade; se sobrar mais de um,
  mostra os dois (o design preve resultado com 1-2 tons).

  A11y: radiogroups com navegacao por setas, aria-checked, resultado em
  aria-live, scroll suave respeitando prefers-reduced-motion.

  Eventos:
  - tone-finder:resolved { detail: { variantIds: [...] } } (bubbles). AUTOMATICO:
    dispara a cada resolucao completa (escolha de faixa, depois cada troca de
    subtom), independente de a cliente ter pedido pra aplicar algo. Nao
    significa escolha.
  - tone-finder:applied { detail: { variantId, variant } } (bubbles). OPT-IN
    (LP do Trio Essencial, Tarefa 9 do plano docs/plans/2026-08-11-lp-trio-essencial.md):
    so existe quando o host tem data-result-mode="apply" (setado por
    snippets/biobio-tone-finder-core.liquid quando renderizado com modo:
    'aplicar'), e so dispara no CLIQUE explicito da cliente no botao "Usar
    este tom" de um resultado. Sem esse atributo, o comportamento default
    (link ancora #tom-<codigo> pro card do guia) fica byte-identico ao de
    antes desta tarefa.
*/
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class BioBioToneFinder extends HTMLElement {
    connectedCallback() {
      // O upgrade do custom element pode rodar ANTES dos filhos existirem
      // (visto no Chromium mobile via Playwright): adia a inicializacao
      // ate o parse completo.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      } else {
        this.init();
      }
    }

    init() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'true';

      // Modo opt-in (Tarefa 9): so true quando o host tem
      // data-result-mode="apply", escrito pelo snippet quando modo: 'aplicar'
      // e passado. Sem o atributo, applyMode fica false e o resultado
      // continua sendo o link ancora de sempre.
      this.applyMode = this.dataset.resultMode === 'apply';

      try {
        this.variants = JSON.parse(this.querySelector('[data-tone-variants]').textContent);
      } catch (error) {
        this.variants = [];
      }
      this.byCodigo = {};
      this.variants.forEach((v) => {
        const match = (v.titulo || '').match(/#(\d{3})/);
        if (match) this.byCodigo[match[1]] = v;
      });

      this.stepSubtom = this.querySelector('[data-step="subtom"]');
      this.resultEl = this.querySelector('[data-result]');
      this.template = this.querySelector('[data-result-template]');
      this.faixa = null;
      this.subtom = null;

      this.paintFaixaSwatches();
      this.bindGroup(this.querySelectorAll('[data-faixa]'), (btn) => {
        this.faixa = btn.dataset.faixa.split(',');
        this.stepSubtom.hidden = false;
        this.resolve();
        this.scrollTo(this.stepSubtom);
      });
      this.bindGroup(this.querySelectorAll('[data-subtom]'), (btn) => {
        this.subtom = btn.dataset.subtom;
        this.resolve();
      });
    }

    paintFaixaSwatches() {
      this.querySelectorAll('[data-faixa-swatches]').forEach((wrap) => {
        wrap.dataset.faixaSwatches.split(',').forEach((codigo) => {
          const v = this.byCodigo[codigo];
          if (!v) return;
          const dot = document.createElement('span');
          dot.className = 'tone-finder__dot';
          dot.style.background = v.hex;
          wrap.appendChild(dot);
        });
      });
    }

    bindGroup(buttons, onPick) {
      const list = Array.from(buttons);
      list.forEach((btn, index) => {
        btn.tabIndex = index === 0 ? 0 : -1;
        btn.addEventListener('click', () => {
          list.forEach((b) => {
            b.setAttribute('aria-checked', String(b === btn));
            b.tabIndex = b === btn ? 0 : -1;
          });
          onPick(btn);
        });
        btn.addEventListener('keydown', (event) => {
          const moves = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
          if (!(event.key in moves)) return;
          event.preventDefault();
          const next = list[(index + moves[event.key] + list.length) % list.length];
          next.focus();
          next.click();
        });
      });
    }

    affinity(v, subtom) {
      const s = (v.subtom || '').toLowerCase();
      if (subtom === 'quente') return s.includes('quente') ? 2 : s.includes('neutro') ? 1 : 0;
      if (subtom === 'frio') return s.includes('frio') ? 2 : s.includes('neutro') ? 1 : 0;
      return s.includes('neutro') ? 2 : 1;
    }

    resolve() {
      if (!this.faixa) return;
      let candidates = this.faixa.map((c) => this.byCodigo[c]).filter(Boolean);
      if (this.subtom && candidates.length > 1) {
        const best = Math.max(...candidates.map((v) => this.affinity(v, this.subtom)));
        const filtered = candidates.filter((v) => this.affinity(v, this.subtom) === best);
        if (filtered.length) candidates = filtered;
      }
      this.render(candidates.slice(0, 2));
    }

    render(matches) {
      if (!matches.length) return;
      this.resultEl.hidden = false;
      this.resultEl.innerHTML = '';
      this.resultEl.appendChild(this.template.content.cloneNode(true));
      const wrap = this.resultEl.querySelector('[data-matches]');

      matches.forEach((v) => {
        const card = document.createElement('div');
        card.className = 'tone-finder__match';
        card.innerHTML =
          '<span class="tone-finder__match-swatch" aria-hidden="true"></span>' +
          '<div class="tone-finder__match-body">' +
          '<p class="tone-finder__match-title"></p>' +
          '<p class="tone-finder__match-desc"></p>' +
          (this.applyMode
            ? '<button type="button" class="tone-finder__match-cta" data-tone-apply></button>'
            : '<a class="tone-finder__match-cta"></a>') +
          '</div>';
        card.querySelector('.tone-finder__match-swatch').style.background = v.hex;
        card.querySelector('.tone-finder__match-title').textContent = v.titulo;
        card.querySelector('.tone-finder__match-desc').textContent = v.descricao;
        const cta = card.querySelector('.tone-finder__match-cta');

        if (this.applyMode) {
          // Aplicacao explicita (Tarefa 9): um botao por resultado, nunca
          // navegacao. So dispara tone-finder:applied no CLIQUE da cliente;
          // tone-finder:resolved (abaixo) continua disparando sozinho a cada
          // calculo e nao decide nada por ela.
          cta.dataset.variantId = v.id;
          cta.textContent = 'Usar este tom';
          cta.addEventListener('click', () => {
            this.dispatchEvent(new CustomEvent('tone-finder:applied', {
              detail: { variantId: v.id, variant: v },
              bubbles: true,
            }));
          });
        } else {
          // Leva pro card do guia (foto aplicada + add-to-cart), nao pra PDP.
          const codigo = (v.titulo.match(/#(\d{3})/) || [])[1] || '';
          cta.href = '#tom-' + codigo;
          cta.setAttribute('data-tone-anchor', '');
          cta.innerHTML = 'Ver tom aplicado'
            + '<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>';
        }
        wrap.appendChild(card);
      });

      if (matches.length === 2 && this.subtom) {
        const note = document.createElement('p');
        note.className = 'tone-finder__note';
        note.textContent = 'Ficou entre os dois? O primeiro é o mais claro. Na dúvida, nosso atendimento ajuda.';
        wrap.appendChild(note);
      }

      if (this.subtom) this.scrollTo(this.resultEl);
      this.dispatchEvent(new CustomEvent('tone-finder:resolved', {
        detail: { variantIds: matches.map((v) => v.id) },
        bubbles: true,
      }));
    }

    scrollTo(el) {
      if (reduceMotion.matches) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  if (!customElements.get('biobio-tone-finder')) {
    customElements.define('biobio-tone-finder', BioBioToneFinder);
  }

  // Regua do tone-guide: ancora suave pro card do tom (delegado; um bind so).
  if (!window.biobioToneAnchorBound) {
    window.biobioToneAnchorBound = true;

    // Animacao propria (easeOutCubic) que RECALCULA o destino a cada frame:
    // se a midia lazy acima do alvo mudar o layout durante o scroll, a curva
    // se adapta e chega exata, sem passar do ponto e voltar (o smooth nativo
    // mirava o layout antigo e exigia correcao posterior).
    const animateScrollTo = (target) => {
      const margem = () => parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
      const destino = () => window.scrollY + target.getBoundingClientRect().top - margem();
      if (reduceMotion.matches) {
        window.scrollTo(0, destino());
        return;
      }
      const inicio = performance.now();
      const yInicial = window.scrollY;
      const duracao = 650;
      const ease = (t) => 1 - Math.pow(1 - t, 3);
      const frame = (agora) => {
        const t = Math.min(1, (agora - inicio) / duracao);
        window.scrollTo(0, yInicial + (destino() - yInicial) * ease(t));
        if (t < 1) requestAnimationFrame(frame);
      };
      requestAnimationFrame(frame);
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-tone-anchor]');
      if (!link) return;
      const target = document.querySelector(link.getAttribute('href'));
      if (!target) return;
      event.preventDefault();
      animateScrollTo(target);
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    });
  }

  // Chips do guia: add-to-cart AJAX (mesmo fluxo do bundle-selector-fb).
  // Nunca checkout direto.
  if (!window.biobioToneCartBound) {
    window.biobioToneCartBound = true;
    document.addEventListener('click', async (event) => {
      const btn = event.target.closest('[data-add-variant]');
      if (!btn || btn.disabled) return;
      const feedback = btn.querySelector('[data-add-feedback]');
      const original = feedback ? feedback.textContent : '';
      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (feedback) feedback.textContent = 'Adicionando…';
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(btn.dataset.addVariant), quantity: 1 }] }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.description || 'HTTP ' + res.status);
        }
        if (window.theme && typeof window.theme.updateCartSummaries === 'function') {
          // Mesmos argumentos do add-to-cart NATIVO do tema: drawer abre so
          // quando a loja configurou openCartDrawerOnMob e a tela e mobile;
          // delayQtyAdjusterShow=false (true e so pra quickbuy e distorcia o
          // drawer). Desktop: atualiza o contador + feedback no botao.
          const abrirDrawer = !!(window.theme.settings
            && window.theme.settings.cartType !== 'page'
            && window.theme.settings.openCartDrawerOnMob
            && window.outerWidth < 1100);
          window.theme.updateCartSummaries(abrirDrawer, true, false);
          if (feedback) feedback.textContent = 'Adicionado!';
          setTimeout(() => {
            if (feedback) feedback.textContent = original;
            btn.disabled = false;
            btn.removeAttribute('aria-busy');
          }, 2000);
        } else {
          window.location.href = '/cart';
        }
      } catch (err) {
        console.error('[tone-guide] falha ao adicionar ao carrinho', err);
        if (feedback) feedback.textContent = 'Tente de novo';
        setTimeout(() => {
          if (feedback) feedback.textContent = original;
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        }, 2500);
      }
    });
  }
})();
